import { lazy, Suspense, useEffect, useState } from "react";
import { api, inr, fmtDate } from "@/lib/api";
import PageHeader, { Skeleton } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { useCountUp } from "@/hooks/useCountUp";
import {
  Sparkle, TrendUp, Package, Truck, Warning, ArrowsClockwise, CurrencyInr,
  Stack, Circuitry, ArrowUpRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const RevenueChart = lazy(() => import("@/components/charts/RevenueChart"));
const OrdersByStatusChart = lazy(() => import("@/components/charts/OrdersByStatusChart"));

const KPI_CFG = [
  { key: "revenue", label: "Confirmed revenue", icon: CurrencyInr, fmt: inr },
  { key: "orders", label: "Total orders", icon: Package, fmt: (v) => String(v) },
  { key: "aov", label: "Avg order value", icon: TrendUp, fmt: inr },
  { key: "inventory_value_fg", label: "Finished goods value", icon: Stack, fmt: inr },
  { key: "low_stock_items", label: "Low stock items", icon: Warning, fmt: (v) => String(v), tone: "warn" },
  { key: "exchange_rate", label: "Exchange rate", icon: ArrowsClockwise, fmt: (v) => `${(Number(v || 0) * 100).toFixed(1)}%` },
  { key: "outstanding_po", label: "Outstanding PO", icon: Truck, fmt: inr },
  { key: "inventory_value_raw", label: "Raw material value", icon: Circuitry, fmt: inr },
];

function KpiCard({ label, value, icon: Icon, fmt, tone }) {
  // Only animate numeric values under 1 billion (fmt = inr / String)
  const num = Number(value) || 0;
  const animate = num > 0 && Number.isFinite(num) && num < 1_000_000_000;
  const shown = useCountUp(animate ? num : 0, { duration: 850 });
  const displayValue = animate ? fmt(shown) : fmt(value);
  return (
    <div className="card-elevated group" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone === "warn" ? "bg-[color:hsl(var(--warning-soft))] text-[color:hsl(var(--warning))]" : "bg-muted text-muted-foreground"}`}>
          <Icon size={15} weight="regular" />
        </div>
      </div>
      <div className="metric-num mt-5 text-foreground">{displayValue}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [genBusy, setGenBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: d }, { data: ins }] = await Promise.all([
        api.get("/analytics/dashboard"),
        api.get("/analytics/insights").catch(() => ({ data: [] })),
      ]);
      setData(d); setInsights(ins);
    } catch (_) { toast.error("Could not load dashboard"); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data: r } = await api.post("/analytics/insights/generate");
      toast.success(`Generated ${r.count} AI insights`);
      setInsights(r.insights);
    } catch (_) { toast.error("AI generation failed"); }
    setGenBusy(false);
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Executive"
        title="Dashboard"
        description="A calm overview of sales, production, inventory, and exchanges — updated as your teams work."
        actions={
          <button onClick={generate} disabled={genBusy} className="btn-primary" data-testid="generate-insights-btn">
            <Sparkle size={15} weight="fill" />
            {genBusy ? "Generating…" : "Generate AI insights"}
          </button>
        }
      />

      {!data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5" data-testid="dashboard-loading">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="card-elevated">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-9 w-32 mt-6" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 stagger">
            {KPI_CFG.map(({ key, label, icon, fmt, tone }) => (
              <KpiCard
                key={key}
                label={label}
                value={data.kpi[key]}
                icon={icon}
                fmt={fmt}
                tone={tone}
              />
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-5 mt-6">
            <div className="card-elevated lg:col-span-2 min-h-[320px]" data-testid="revenue-chart">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="label-eyebrow">Revenue</div>
                  <div className="display-3 mt-1">Last 14 days</div>
                </div>
                <span className="text-xs text-muted-foreground">Confirmed orders</span>
              </div>
              <div className="h-64 mt-4">
                <Suspense fallback={<Skeleton className="h-full w-full" />}>
                  <RevenueChart data={data.revenue_series || []} />
                </Suspense>
              </div>
            </div>
            <div className="card-elevated min-h-[320px]" data-testid="orders-by-status">
              <div className="label-eyebrow">Orders</div>
              <div className="display-3 mt-1">By status</div>
              <div className="h-64 mt-4">
                <Suspense fallback={<Skeleton className="h-full w-full" />}>
                  <OrdersByStatusChart data={data.orders_by_status || []} />
                </Suspense>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 mt-6">
            <div className="card-brand lg:col-span-2" data-testid="ai-insights-panel">
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-eyebrow flex items-center gap-2">
                    <Sparkle size={11} weight="fill" className="text-[color:hsl(var(--brand))]" />
                    Claude Sonnet 4.5
                  </div>
                  <div className="display-3 mt-1">AI observations</div>
                </div>
                <span className="hidden md:inline text-[11px] text-muted-foreground max-w-[240px] text-right">Labelled with kind &amp; confidence — never presented as fact</span>
              </div>
              <div className="mt-5 space-y-3">
                {(!insights || insights.length === 0) && (
                  <div className="text-sm text-muted-foreground py-4">
                    No insights yet. Click <span className="font-medium text-foreground">Generate AI insights</span> to synthesize your latest data.
                  </div>
                )}
                {insights.slice(0, 5).map((i) => (
                  <div key={i._id} className="border border-border/70 rounded-[calc(var(--radius)-2px)] p-4 bg-card transition-all duration-200 hover:border-border hover:shadow-[var(--shadow-xs)]" data-testid={`insight-${i.kind}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusChip value={i.kind} />
                      <span className="label-eyebrow">{i.module}</span>
                      <span className="text-[11px] text-muted-foreground ml-auto tabular">{(i.confidence * 100).toFixed(0)}% confidence</span>
                    </div>
                    <div className="font-display text-[17px] font-semibold mt-2 leading-snug tracking-tight">{i.title}</div>
                    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{i.body}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-elevated" data-testid="integration-mini">
              <div className="label-eyebrow">Integration health</div>
              <div className="display-3 mt-1">Website ↔ ERP</div>
              <div className="mt-5 space-y-3 text-sm">
                <Row label="Status" value={<StatusChip value={data.integration_health?.connected ? "ok" : "failed"} />} />
                <Row label="Last order" value={fmtDate(data.integration_health?.last_order_received)} />
                <Row label="Last product sync" value={fmtDate(data.integration_health?.last_product_update)} />
                <Row label="Failed webhooks" value={data.integration_health?.failed_webhooks || 0} />
                <Row label="Duplicates blocked" value={data.integration_health?.duplicate_attempts || 0} />
                <Row label="Latency" value={`${data.integration_health?.avg_latency_ms || 0} ms`} />
              </div>
              <Link to="/integration" className="btn-ghost w-full mt-6 group" data-testid="open-integration">
                Open integration
                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 py-2 last:border-b-0">
      <span className="label-eyebrow">{label}</span>
      <span className="text-sm tabular">{value}</span>
    </div>
  );
}
